import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TableModule } from 'primeng/table';
import { catchError, throwError } from 'rxjs';
import uuid4 from 'uuid4';
import { HttpResponse } from '../../../../../model/HttpResponse';
import { Modalidad } from '../../../../../model/Modalidad';
import { AcademyAdministratorOperation } from '../../../../../model/administrator/AcademyAdministratorOperation';
import { AcademyAdministratorType } from '../../../../../model/administrator/AcademyAdministratorType';
import { AdministratorEntity } from '../../../../../model/administrator/AdministratorEntity';
import { Competition, EventAmountItem, IdObject } from '../../../../../model/competitions/Competition';
import { Event } from '../../../../../model/competitions/Event';
import { Category } from '../../../../../model/competitions/Category';
import { CategoryLevel } from '../../../../../model/competitions/Level';
import { CompetitionType } from '../../../../../model/competitions/competitionType';
import { CrosselingItem } from '../../../../../model/Crosseling/CrosselingItem';
import { CompetitionService } from '../../../../../services/competition.service';
import { LoadingService } from '../../../../../services/loading.service';

@Component({
  selector: 'app-artistico-competition-form',
  standalone: true,
  imports: [
    InputTextModule,
    InputTextareaModule,
    SelectButtonModule,
    CommonModule,
    DialogModule,
    ReactiveFormsModule,
    FormsModule,
    MultiSelectModule,
    InputNumberModule,
    ConfirmDialogModule,
    ButtonModule,
    TableModule,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './artistico-competition-form.component.html',
  styleUrl: './artistico-competition-form.component.scss',
})
export class ArtisticoCompetitionFormComponent implements OnInit {
  validateForm!: FormGroup;
  showForm: boolean = false;
  dialogHeader: string = '';

  @Input()
  isUpdateForm: boolean = false;

  @Input()
  competitionToUpdate!: Competition;

  @Input()
  levels!: CategoryLevel[];

  @Input()
  categories!: Category[];

  @Input()
  events!: Event[];

  eventsFormArray!: FormArray;

  @Output()
  onCompetitionCreated: EventEmitter<Competition> =
    new EventEmitter<Competition>();

  @Output()
  onFormClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

  competitionTypes: { label: string; value: CompetitionType }[] = [
    { label: 'Vinculados', value: CompetitionType.MEMBERS },
    { label: 'Externos', value: CompetitionType.EXTERNAL },
    { label: 'Vinculados y Externos', value: CompetitionType.MEMBERS_AND_EXTERNAL },
  ];

  artisticoLevels: CategoryLevel[] = [];
  artisticoCategories: Category[] = [];
  filteredCategories: Category[] = [];
  artisticoEvents: Event[] = [];
  selectedLevels: CategoryLevel[] = [];
  selectedCategories: Category[] = [];
  selectedEvents: Event[] = [];
  
  stateOptions: any[] = [
    { label: 'No', value: false },
    { label: 'Si', value: true },
  ];

  crosselingItems: CrosselingItem[] = [];
  showCrosselingDialog: boolean = false;
  editingCrosselingItem: CrosselingItem | null = null;
  editingIndex: number = -1;

  constructor(
    private fb: FormBuilder,
    private loadingService: LoadingService,
    private competitionService: CompetitionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.filterDataByModality();
    if (this.isUpdateForm) {
      this.setSelectionsFromCompetition(this.competitionToUpdate);
      this.getEditForm(this.competitionToUpdate);
    } else {
      this.getFormNew();
    }
    this.showForm = true;
  }

  filterDataByModality(): void {
    this.artisticoLevels = this.levels.filter(level => 
      this.categories.some(category => 
        category.idCategoryLevel === level.id && 
        category.modality === Modalidad.ARTISTICO
      )
    );
    
    this.artisticoCategories = this.categories.filter(category => 
      category.modality === Modalidad.ARTISTICO
    );
    
    this.filteredCategories = [...this.artisticoCategories];

    this.artisticoEvents = this.events.filter(event =>
      event.modality === Modalidad.ARTISTICO && event.isActive
    );
  }

  setSelectionsFromCompetition(competition: Competition): void {
    // Set selected levels
    if (competition.levels) {
      this.selectedLevels = this.artisticoLevels.filter(level =>
        competition.levels!.some(l => l.id === level.id)
      );
    }

    // Set selected categories
    this.selectedCategories = this.artisticoCategories.filter(category =>
      competition.categories.some(c => c.id === category.id)
    );

    // Set selected events - now events are complete objects
    if (competition.events) {
      this.selectedEvents = competition.events.filter(event =>
        event.modality === Modalidad.ARTISTICO
      );
    }
  }

  getFormNew(): void {
    this.eventsFormArray = this.fb.array([]);
    
    this.validateForm = this.fb.group({
      type: [null, [Validators.required]],
      name: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      location: [null, [Validators.required]],
      baseDateToCalculateAge: [null, [Validators.required]],
      levels: [null, [Validators.required]],
      categories: [null, [Validators.required]],
      eventsToCreate: this.eventsFormArray,
      ordinaryStartDate: [null, [Validators.required]],
      ordinaryEndDate: [null, [Validators.required]],
      extraordinaryStartDate: [null, [Validators.required]],
      extraordinaryEndDate: [null, [Validators.required]],
      ordinaryAthleteAmounts: this.fb.array([]),
      extraordinaryAthleteAmounts: this.fb.array([]),
      ordinaryClubAmount: [null, [Validators.required]],
      extraordinaryClubAmount: [null, [Validators.required]],
      urlResolution: [null],
      urlImage: [null],
      isOpenToRegister: [false, [Validators.required]],
      isPublished: [false, [Validators.required]],
      digitalPaymentAvailable: [true, [Validators.required]],
      manualPaymentAvailable: [false, [Validators.required]],
      hasCrosselling: [false, [Validators.required]],
    });

    this.initializeEventAmounts();
    this.setupLevelsListener();
    this.dialogHeader = `Nuevo evento de la modalidad ARTÍSTICO`;
    this.isUpdateForm = false;
    this.showForm = true;
  }

  getEditForm(item: Competition): void {
    this.eventsFormArray = this.fb.array([]);
    this.loadExistingEventsToFormArray();
    
    this.validateForm = this.fb.group({
      id: [item.id, [Validators.required]],
      type: [item.type, [Validators.required]],
      name: [item.name, [Validators.required]],
      startDate: [item.startDate, [Validators.required]],
      endDate: [item.endDate, [Validators.required]],
      location: [item.location, [Validators.required]],
      baseDateToCalculateAge: [item.baseDateToCalculateAge, [Validators.required]],
      levels: [this.selectedLevels, [Validators.required]],
      categories: [this.selectedCategories, [Validators.required]],
      eventsToEdit: this.eventsFormArray,
      ordinaryStartDate: [item.registerDates.ordinary.start, [Validators.required]],
      ordinaryEndDate: [item.registerDates.ordinary.end, [Validators.required]],
      extraordinaryStartDate: [item.registerDates.extraordinary.start, [Validators.required]],
      extraordinaryEndDate: [item.registerDates.extraordinary.end, [Validators.required]],
      ordinaryAthleteAmounts: this.fb.array([]),
      extraordinaryAthleteAmounts: this.fb.array([]),
      ordinaryClubAmount: [item.registerAmounts.ordinary.club, [Validators.required]],
      extraordinaryClubAmount: [item.registerAmounts.extraordinary.club, [Validators.required]],
      urlResolution: [item.urlResolution || ''],
      urlImage: [item.urlImage || ''],
      isOpenToRegister: [item.isOpenToRegister, [Validators.required]],
      isPublished: [item.isPublished, [Validators.required]],
      digitalPaymentAvailable: [item.payment.digitalPaymentAvailable, [Validators.required]],
      manualPaymentAvailable: [item.payment.manualPaymentAvailable, [Validators.required]],
      hasCrosselling: [item.hasCrosselling || false, [Validators.required]],
    });

    this.loadExistingEventAmounts(item);
    this.setupLevelsListener();
    this.filterCategoriesBySelectedLevels();

    // Cargar items de venta cruzada si existen
    if (item.customData?.crosselingItems) {
      this.crosselingItems = [...item.customData.crosselingItems];
    }

    this.dialogHeader = `Editar evento ${item.name}`;
    this.isUpdateForm = true;
    this.showForm = true;
  }

  initializeEventAmounts(): void {
    // Initialize with default amounts for 1, 2, and 3 events
    const defaultAmounts = [
      { quantity: 1, amount: 0 },
      { quantity: 2, amount: 0 },
      { quantity: 3, amount: 0 }
    ];

    const ordinaryArray = this.validateForm.get('ordinaryAthleteAmounts') as FormArray;
    const extraordinaryArray = this.validateForm.get('extraordinaryAthleteAmounts') as FormArray;

    defaultAmounts.forEach(amount => {
      ordinaryArray.push(this.createEventAmountFormGroup(amount));
      extraordinaryArray.push(this.createEventAmountFormGroup(amount));
    });
  }

  loadExistingEventAmounts(competition: Competition): void {
    const ordinaryArray = this.validateForm.get('ordinaryAthleteAmounts') as FormArray;
    const extraordinaryArray = this.validateForm.get('extraordinaryAthleteAmounts') as FormArray;

    // Load ordinary amounts
    const ordinaryAmounts = Array.isArray(competition.registerAmounts.ordinary.athlete) 
      ? competition.registerAmounts.ordinary.athlete as EventAmountItem[]
      : [{ quantity: 1, amount: competition.registerAmounts.ordinary.athlete as number }];

    ordinaryAmounts.forEach(amount => {
      ordinaryArray.push(this.createEventAmountFormGroup(amount));
    });

    // Load extraordinary amounts
    const extraordinaryAmounts = Array.isArray(competition.registerAmounts.extraordinary.athlete) 
      ? competition.registerAmounts.extraordinary.athlete as EventAmountItem[]
      : [{ quantity: 1, amount: competition.registerAmounts.extraordinary.athlete as number }];

    extraordinaryAmounts.forEach(amount => {
      extraordinaryArray.push(this.createEventAmountFormGroup(amount));
    });
  }

  createEventAmountFormGroup(amount: EventAmountItem): FormGroup {
    return this.fb.group({
      quantity: [amount.quantity, [Validators.required, Validators.min(1)]],
      amount: [amount.amount, [Validators.required, Validators.min(0)]]
    });
  }

  get ordinaryAthleteAmounts(): FormArray {
    return this.validateForm.get('ordinaryAthleteAmounts') as FormArray;
  }

  get extraordinaryAthleteAmounts(): FormArray {
    return this.validateForm.get('extraordinaryAthleteAmounts') as FormArray;
  }

  get eventsToCreate(): FormArray {
    return this.validateForm.get('eventsToCreate') as FormArray;
  }

  get eventsToEdit(): FormArray {
    return this.validateForm.get('eventsToEdit') as FormArray;
  }

  addEventAmount(type: 'ordinary' | 'extraordinary'): void {
    const array = type === 'ordinary' ? this.ordinaryAthleteAmounts : this.extraordinaryAthleteAmounts;
    const newAmount = { quantity: array.length + 1, amount: 0 };
    array.push(this.createEventAmountFormGroup(newAmount));
  }

  removeEventAmount(type: 'ordinary' | 'extraordinary', index: number): void {
    const array = type === 'ordinary' ? this.ordinaryAthleteAmounts : this.extraordinaryAthleteAmounts;
    if (array.length > 1) {
      array.removeAt(index);
    }
  }

  addEvent(): void {
    const eventFormGroup = this.fb.group({
      name: ['', [Validators.required]],
      description: ['']
    });
    if (this.isUpdateForm) {
      this.eventsToEdit.push(eventFormGroup);
    } else {
      this.eventsToCreate.push(eventFormGroup);
    }
  }

  removeEvent(index: number): void {
    if (this.isUpdateForm) {
      if (this.eventsToEdit.length > 0) {
        this.eventsToEdit.removeAt(index);
      }
    } else {
      if (this.eventsToCreate.length > 0) {
        this.eventsToCreate.removeAt(index);
      }
    }
  }

  loadExistingEventsToFormArray(): void {
    if (this.selectedEvents && this.selectedEvents.length > 0) {
      this.selectedEvents.forEach(event => {
        const eventFormGroup = this.fb.group({
          id: [event.id],
          name: [event.name, [Validators.required]],
          description: [event.description || '']
        });
        this.eventsFormArray.push(eventFormGroup);
      });
    }
  }

  createEventFromForm(eventForm: any): Event {
    return {
      id: uuid4(),
      name: eventForm.name,
      description: eventForm.description || '',
      modality: Modalidad.ARTISTICO,
      isActive: true
    };
  }

  saveCompetition(): void {
    if (this.validateForm.valid) {
      this.loadingService.showLoading();
      const competition = this.buildCompetition(this.validateForm.value);
      const entity: AdministratorEntity = {
        operation: AcademyAdministratorOperation.CREATE,
        type: AcademyAdministratorType.COMPETIIONS,
        payload: competition,
      };

      this.competitionService
        .sendRequest(entity)
        .pipe(catchError(this.handleError.bind(this)))
        .subscribe((res: HttpResponse) => {
          if (res.code === 200) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Competición ARTÍSTICO creada correctamente',
            });
            this.showForm = false;
            this.onCompetitionCreated.emit(entity.payload);
            this.loadingService.hideLoading();
          }
        });
    }
  }

  updateCompetition(): void {
    if (this.validateForm.valid) {
      this.confirmationService.confirm({
        message: '¿Estás seguro de actualizar el evento ARTÍSTICO?',
        header: 'Confirmar',
        acceptLabel: 'Sí',
        rejectLabel: 'No',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.loadingService.showLoading();
          const competition = this.buildCompetition(this.validateForm.value);
          const entity: AdministratorEntity = {
            operation: AcademyAdministratorOperation.UPDATE,
            type: AcademyAdministratorType.COMPETIIONS,
            payload: competition,
          };

          this.competitionService
            .sendRequest(entity)
            .pipe(catchError(this.handleError.bind(this)))
            .subscribe((res: HttpResponse) => {
              if (res.code === 200) {
                this.messageService.add({
                  severity: 'success',
                  summary: 'Éxito',
                  detail: 'Evento ARTÍSTICO actualizado correctamente',
                });
                this.showForm = false;
                this.onCompetitionCreated.emit(entity.payload);
                this.loadingService.hideLoading();
              }
            });
        },
      });
    }
  }

  buildCompetition(formValue: any): Competition {
    const ordinaryAmounts: EventAmountItem[] = formValue.ordinaryAthleteAmounts;
    const extraordinaryAmounts: EventAmountItem[] = formValue.extraordinaryAthleteAmounts;

    let events: Event[] = [];
    
    if (this.isUpdateForm && formValue.eventsToEdit) {
      events = formValue.eventsToEdit.map((eventForm: any) => {
        if (eventForm.id) {
          // Evento existente editado
          return {
            id: eventForm.id,
            name: eventForm.name,
            description: eventForm.description || '',
            modality: Modalidad.ARTISTICO,
            isActive: true
          };
        } else {
          // Nuevo evento creado en modo edición
          return this.createEventFromForm(eventForm);
        }
      });
    } else if (!this.isUpdateForm && formValue.eventsToCreate) {
      events = formValue.eventsToCreate.map((eventForm: any) => 
        this.createEventFromForm(eventForm)
      );
    }

    const competition: Competition = {
      id: this.isUpdateForm ? formValue.id : uuid4(),
      type: formValue.type,
      name: formValue.name,
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      location: formValue.location,
      baseDateToCalculateAge: formValue.baseDateToCalculateAge,
      modality: Modalidad.ARTISTICO,
      levels: formValue.levels.map((level: CategoryLevel) => ({ id: level.id })),
      categories: formValue.categories.map((category: Category) => ({ id: category.id })),
      events: events,
      registerDates: {
        ordinary: {
          start: formValue.ordinaryStartDate,
          end: formValue.ordinaryEndDate,
        },
        extraordinary: {
          start: formValue.extraordinaryStartDate,
          end: formValue.extraordinaryEndDate,
        },
      },
      registerAmounts: {
        ordinary: {
          athlete: ordinaryAmounts,
          club: formValue.ordinaryClubAmount,
        },
        extraordinary: {
          athlete: extraordinaryAmounts,
          club: formValue.extraordinaryClubAmount,
        },
      },
      payment: {
        digitalPaymentAvailable: formValue.digitalPaymentAvailable,
        manualPaymentAvailable: formValue.manualPaymentAvailable,
        paymentByEvents: true,
      },
      urlResolution: formValue.urlResolution,
      urlImage: formValue.urlImage,
      urlToRegister: '',
      isOpenToRegister: formValue.isOpenToRegister,
      isPublished: formValue.isPublished,
      hasCrosselling: formValue.hasCrosselling || false,
      customData: formValue.hasCrosselling && this.crosselingItems.length > 0 ? {
        crosselingItems: this.crosselingItems
      } : undefined,
    };

    return competition;
  }

  closeDialog(): void {
    this.validateForm.reset();
    this.crosselingItems = [];
    this.onFormClosed.emit(true);
    this.showForm = false;
  }

  addCrosselingItem() {
    this.editingCrosselingItem = {
      id: '',
      name: '',
      description: '',
      amount: 0,
      urlImage: ''
    };
    this.editingIndex = -1;
    this.showCrosselingDialog = true;
  }

  editCrosselingItem(item: CrosselingItem, index: number) {
    this.editingCrosselingItem = { ...item };
    this.editingIndex = index;
    this.showCrosselingDialog = true;
  }

  saveCrosselingItem() {
    if (this.editingCrosselingItem) {
      if (this.editingIndex === -1) {
        this.editingCrosselingItem.id = uuid4();
        this.crosselingItems.push(this.editingCrosselingItem);
      } else {
        this.crosselingItems[this.editingIndex] = this.editingCrosselingItem;
      }
      this.closeCrosselingDialog();
    }
  }

  deleteCrosselingItem(index: number) {
    this.confirmationService.confirm({
      message: '¿Estás seguro de eliminar este item de venta cruzada?',
      header: 'Confirmar',
      acceptLabel: 'Sí',
      rejectLabel: 'No',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.crosselingItems.splice(index, 1);
      }
    });
  }

  closeCrosselingDialog() {
    this.showCrosselingDialog = false;
    this.editingCrosselingItem = null;
    this.editingIndex = -1;
  }

  handleError(error: HttpErrorResponse) {
    this.loadingService.hideLoading();
    if (error.status === 422) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede procesar este evento ARTÍSTICO, puede que ya exista un evento con el mismo nombre',
      });
      console.error('An error occurred:', error.error);
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error inesperado, por favor intenta de nuevo',
      });
      this.validateForm.reset();
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  private setupLevelsListener(): void {
    this.validateForm.get('levels')?.valueChanges.subscribe((selectedLevels: CategoryLevel[]) => {
      this.selectedLevels = selectedLevels || [];
      this.filterCategoriesBySelectedLevels();
      this.clearCategoriesSelection();
    });
  }

  private filterCategoriesBySelectedLevels(): void {
    if (!this.selectedLevels || this.selectedLevels.length === 0) {
      this.filteredCategories = [...this.artisticoCategories];
    } else {
      const selectedLevelIds = this.selectedLevels.map(level => level.id);
      this.filteredCategories = this.artisticoCategories.filter(category =>
        selectedLevelIds.includes(category.idCategoryLevel)
      );
    }
  }

  private clearCategoriesSelection(): void {
    const currentCategories = this.validateForm.get('categories')?.value || [];
    const validCategories = currentCategories.filter((category: Category) =>
      this.filteredCategories.some(filtered => filtered.id === category.id)
    );
    
    this.validateForm.get('categories')?.setValue(validCategories);
    this.selectedCategories = validCategories;
  }
}